import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { fetchAllAnalysis } from 'src/api/analysis/list'
import { PATH_DASHBOARD } from 'src/routes/paths'

// ----------------------------------------------------------------------
// Batches, Phase 1: there is no batch record on the server. Every analysis
// started as part of a batch carries a top-level `batch: { id, name, sampleKey }`
// in its start body, which libra stores verbatim as
// AnalysisWrapper.analysis_parameters, and /analysis/list returns that field per
// row. A batch is therefore the group of recent analyses sharing
// analysis_parameters.batch.id.
//
// The list is read with one large page (the server's limit check is commented
// out), so only batches within the latest BATCH_SCAN_LIMIT analyses are known.
// ----------------------------------------------------------------------

export const BATCHES_QUERY_KEY = 'analysis-batches'
export const BATCH_SCAN_LIMIT = 500
// Poll only while some batch is unfinished.
const POLL_MS = 30 * 1000

export const batchPath = (batchId) => `${PATH_DASHBOARD.general.batches}/${encodeURIComponent(batchId)}`

export const getRowBatch = (row) => {
  const batch = row?.analysis_parameters?.batch
  if (!batch || typeof batch !== 'object' || batch.id === undefined || batch.id === null || batch.id === '') {
    return null
  }
  return batch
}

// TODO(batches): the backend reports failed pipelines as RUNNING on purpose
// (analysis_wrapper_service._apply_failed_guard) and refunds them out of band, so
// v1 can only tell Running from Done. Add a Failed / Under review state once the
// server exposes one; do not infer it here.
export const rowState = (row) => (row?.status === 'DONE' ? 'done' : 'running')

export const isSomaticRow = (row) => {
  const annotation = row?.analysis_parameters?.annotation || {}
  return (
    (Array.isArray(row?.tags) && row.tags.includes('Somatic')) ||
    annotation.analysisType === 'TumorOnly' ||
    annotation.analysisType === 'TumorNormal' ||
    annotation.isSomatic === true
  )
}

const SEQUENCING_LABELS = {
  'Whole Exome': 'Whole exome',
  'Clinical Exome': 'Clinical exome',
}

const kitLabel = (panel) =>
  typeof panel === 'string' && panel.trim() ? panel.trim().replace(/\.bed(\.gz)?$/i, '') : null

/**
 * Short workflow description of one start body, read the way the batch was set
 * up: "Whole exome · Twist Exome v2 · CNV". The kind (Germline / Somatic) is kept
 * apart so cards can show it as an icon instead of a word.
 */
export const describeRowWorkflow = (row) => {
  const params = row?.analysis_parameters || {}
  const annotation = params.annotation || {}
  const input = row?.analysis_type || params.annotation_type || ''
  const somatic = isSomaticRow(row)
  const parts = []

  if (input === 'FASTQ') {
    if (somatic) parts.push(annotation.analysisType === 'TumorNormal' ? 'Tumour-normal' : 'Tumour-only')
    if (annotation.type === 'WholeGenome') parts.push('Whole genome')
    else if (annotation.targetedType) parts.push(SEQUENCING_LABELS[annotation.targetedType] || annotation.targetedType)
    const kit = annotation.type === 'WholeGenome' ? null : kitLabel(annotation.panel)
    if (kit) parts.push(kit)
    if (annotation.runCnv) parts.push('CNV')
  } else if (input === 'ONT_BAM' || input === 'PACBIO_BAM') {
    parts.push(input === 'ONT_BAM' ? 'ONT' : 'PacBio')
    if (annotation.runCnv) parts.push('CNV')
    if (annotation.runSv) parts.push('SV')
    if (annotation.runMod) parts.push('Methylation')
  } else if (input === 'VCF') {
    parts.push('VCF')
    if (annotation.reference && annotation.reference !== 'None') parts.push(annotation.reference)
  } else if (input) {
    parts.push(input)
  }
  // Same words as describeWorkflow in src/services/analysisStart.js.
  if (annotation.runAI && !somatic) parts.push('AI-assisted')

  return {
    kind: somatic ? 'somatic' : 'germline',
    kindLabel: somatic ? 'Somatic' : 'Germline',
    short: parts.join(' · '),
  }
}

const timeOf = (row) => {
  const t = new Date(row?.created_at).getTime()
  return Number.isFinite(t) ? t : 0
}

/**
 * Groups analysis list rows into batches, newest first. Pure, so it is easy to
 * test and cheap to memoise.
 */
export const groupBatches = (rows = []) => {
  const byId = new Map()
  rows.forEach((row) => {
    const batch = getRowBatch(row)
    if (!batch) return
    const id = String(batch.id)
    if (!byId.has(id)) byId.set(id, { id, meta: batch, analyses: [] })
    byId.get(id).analyses.push(row)
  })

  return Array.from(byId.values())
    .map(({ id, meta, analyses }) => {
      const sorted = [...analyses].sort((a, b) => timeOf(a) - timeOf(b))
      const done = sorted.filter((row) => rowState(row) === 'done').length
      const total = sorted.length
      const workflow = describeRowWorkflow(sorted[0])
      // A summary written by the start module wins over the one read back from
      // the payload.
      const storedSummary =
        typeof meta.workflowSummary === 'string' && meta.workflowSummary.trim() ? meta.workflowSummary.trim() : null
      // Cards show the kind as an icon, so the short line drops the leading word.
      const storedShort = storedSummary
        ? storedSummary.replace(new RegExp(`^${workflow.kindLabel}( · |$)`), '').trim()
        : null
      const workflowShort = storedSummary ? storedShort : workflow.short
      return {
        id,
        name: (typeof meta.name === 'string' && meta.name.trim()) || 'Untitled batch',
        kind: workflow.kind,
        workflowSummary: storedSummary || [workflow.kindLabel, workflowShort].filter(Boolean).join(' · '),
        workflowShort: workflowShort || workflow.kindLabel,
        analyses: sorted,
        counts: { total, done, running: total - done },
        startedAt: timeOf(sorted[0]),
        lastStartedAt: timeOf(sorted[sorted.length - 1]),
        finished: total > 0 && done === total,
      }
    })
    .sort((a, b) => b.startedAt - a.startedAt)
}

const fetchRecentAnalyses = () => fetchAllAnalysis({ page: 0, limit: BATCH_SCAN_LIMIT })

/**
 * Batches found in the latest BATCH_SCAN_LIMIT analyses. Polls every 30 s, only
 * while some batch is unfinished and only while a page using it is mounted.
 */
export const useBatches = () => {
  const [poll, setPoll] = useState(false)
  const query = useQuery([BATCHES_QUERY_KEY], fetchRecentAnalyses, {
    staleTime: 20 * 1000,
    refetchInterval: poll ? POLL_MS : false,
    refetchIntervalInBackground: false,
    keepPreviousData: true,
  })

  const batches = useMemo(() => groupBatches(query.data?.results || []), [query.data])
  const anyUnfinished = batches.some((batch) => !batch.finished)
  useEffect(() => {
    setPoll(anyUnfinished)
  }, [anyUnfinished])

  return {
    batches,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: query.refetch,
  }
}

export const useBatch = (batchId) => {
  const { batches, isLoading, isError, isFetching, refetch } = useBatches()
  const batch = useMemo(() => batches.find((b) => b.id === String(batchId)) || null, [batches, batchId])
  // A batch started a moment ago is missing from older cached data until the
  // refetch lands, so it reads as loading rather than not found meanwhile.
  const loading = isLoading || (!batch && isFetching)
  return { batch, isLoading: loading, isError, isFetching, refetch, notFound: !loading && !isError && !batch }
}

// ----------------------------------------------------------------------
// Formatting shared by the batch views

const pad = (n) => String(n).padStart(2, '0')

export const formatClock = (ms) => {
  const d = new Date(ms)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Fixed month names: en-GB short months read "Sept" in some browsers.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "14:02" today, "28 Sep 14:02" this year, "28 Sep 2025 14:02" before.
export const formatWhen = (ms, now = Date.now()) => {
  if (!ms) return ''
  const d = new Date(ms)
  const today = new Date(now)
  if (d.toDateString() === today.toDateString()) return formatClock(ms)
  const year = d.getFullYear() !== today.getFullYear() ? ` ${d.getFullYear()}` : ''
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${year} ${formatClock(ms)}`
}

// "12 min", "2 h 40 min", "3 d 4 h".
export const formatDuration = (ms) => {
  if (!(ms > 0)) return ''
  const minutes = Math.floor(ms / 60000)
  if (minutes < 1) return 'under a minute'
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`
  const days = Math.floor(hours / 24)
  return hours % 24 ? `${days} d ${hours % 24} h` : `${days} d`
}

// "12 done · 36 running", or "48 done" once finished.
export const countsText = ({ done = 0, running = 0 } = {}) => {
  const parts = []
  if (done) parts.push(`${done} done`)
  if (running) parts.push(`${running} running`)
  return parts.join(' · ') || 'Starting'
}

export const pluralise = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
